import type { ApiClient } from './client';

export type EmailFlagKey =
  | 'email_verification_enabled'
  | 'email_verification_required'
  | 'transactional_email_enabled'
  | 'marketing_email_enabled'
  | 'cookie_consent_enabled'
  | 'sensitive_action_email_gate_enabled';

export type EmailMailStatus = {
  mail_enabled: boolean;
  provider: string;
  api_key_configured: boolean;
  from_address: string;
  from_name: string;
  reply_to: string;
  operator_alerts_configured: boolean;
};

export type EmailFlagsMap = Record<EmailFlagKey, boolean>;

export type EmailOpsStatus = {
  status: EmailMailStatus;
  flags: EmailFlagsMap;
  effective: {
    transactional_outbound: boolean;
    email_verification_enabled?: boolean;
    email_verification_required?: boolean;
    marketing_email_enabled?: boolean;
    cookie_consent_enabled?: boolean;
    sensitive_action_email_gate_enabled?: boolean;
  };
  notes?: string[];
};

export type EmailDeliveryRow = {
  id: number;
  user_id: number | null;
  template_key: string;
  subject: string;
  provider: string;
  provider_message_id?: string | null;
  status: string;
  error_code?: string | null;
  error_message?: string | null;
  created_at?: string | null;
};

export type EmailTemplatePreview = {
  template_key: string;
  subject: string;
  html: string;
  text: string;
};

const ENDPOINT = '/admin/email_ops.php';

export const adminEmailMethods = {
  async getEmailOpsStatus(this: ApiClient): Promise<EmailOpsStatus> {
    const response = await this.makeRequest<EmailOpsStatus>(
      `${this.getAdminEndpoint(ENDPOINT)}?action=status`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to load email status');
    }
    return response.data;
  },

  async setEmailFlag(
    this: ApiClient,
    flag: EmailFlagKey,
    enabled: boolean
  ): Promise<{ flags: EmailFlagsMap; effective: { transactional_outbound: boolean } }> {
    const response = await this.makeRequest<{
      flags: EmailFlagsMap;
      effective: { transactional_outbound: boolean };
    }>(this.getAdminEndpoint(ENDPOINT), {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ action: 'set_flag', flag, enabled }),
    });
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to update email flag');
    }
    return response.data;
  },

  async getEmailDeliveryLog(
    this: ApiClient,
    params?: { limit?: number; status?: string }
  ): Promise<EmailDeliveryRow[]> {
    const qs = new URLSearchParams({ action: 'delivery' });
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.status) qs.set('status', params.status);
    const response = await this.makeRequest<{ items: EmailDeliveryRow[] }>(
      `${this.getAdminEndpoint(ENDPOINT)}?${qs.toString()}`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to load delivery log');
    }
    return Array.isArray(response.data.items) ? response.data.items : [];
  },

  async getEmailTemplatePreviews(this: ApiClient): Promise<EmailTemplatePreview[]> {
    const response = await this.makeRequest<{ previews: EmailTemplatePreview[] }>(
      `${this.getAdminEndpoint(ENDPOINT)}?action=previews`,
      { headers: this.getAuthHeaders() }
    );
    if (!response.success || !response.data) {
      throw new Error(response.error || response.message || 'Failed to load email previews');
    }
    return Array.isArray(response.data.previews) ? response.data.previews : [];
  },
};
